import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Navigate, Link } from "react-router-dom";
import portalApi from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { Loader2, ShieldCheck, AlertTriangle, UserPlus } from "lucide-react";

export default function PortalLanding() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const { client, loading, refresh } = usePortalAuth();
    const token = params.get("t") || "";
    const [state, setState] = useState({ status: "checking", error: "" });

    useEffect(() => {
        const run = async () => {
            if (loading) return;
            // If a fresh magic-link token is present, ALWAYS exchange it —
            // even if the browser already holds a portal cookie for a
            // different client. This is the fix for the "every magic link
            // opens the same client's dashboard" bug that admins hit when
            // sharing multiple links from Bulk-Enable.
            if (token) {
                setState({ status: "exchanging" });
                try {
                    // Best-effort: drop any existing session first so the
                    // backend definitely stores the new client's session.
                    try { await portalApi.post("/logout"); } catch { /* noop */ }
                    await portalApi.post("/session", { token });
                    await refresh();
                    // Strip the token from the URL so refreshing the page
                    // doesn't try to re-exchange (and leaks the token in
                    // browser history less obviously).
                    nav("/portal/dashboard", { replace: true });
                } catch (e) {
                    setState({ status: "error",
                               error: e.response?.data?.detail || "This link is invalid or has expired. Please contact HDB for a fresh link." });
                }
                return;
            }
            // No token in URL → fall back to existing session if any.
            if (client) { nav("/portal/dashboard", { replace: true }); return; }
            setState({ status: "no-token" });
        };
        run(); /* eslint-disable-next-line */
    }, [token, client, loading]);

    // Only auto-redirect to dashboard if we're NOT trying to exchange a new
    // token in the URL. Otherwise the redirect fires before the exchange runs.
    if (client && !token) return <Navigate to="/portal/dashboard" replace />;

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-emerald-50/40 to-amber-50/40 p-6"
             data-testid="portal-landing">
            <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-xl p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
                    {state.status === "error"
                        ? <AlertTriangle className="text-red-600" size={28} />
                        : <ShieldCheck className="text-emerald-700" size={28} />}
                </div>
                <h1 className="text-2xl font-black text-slate-900 tracking-tight" style={{fontFamily:"'Chivo',sans-serif"}}>
                    HDB Client Portal
                </h1>
                <p className="text-xs text-slate-500 mt-1 uppercase tracking-widest">Har Din Bachat</p>

                {(state.status === "checking" || state.status === "exchanging" || loading) && (
                    <div className="mt-8 text-slate-600 flex items-center justify-center gap-2" data-testid="portal-verifying">
                        <Loader2 className="animate-spin" size={16} />
                        <span>Verifying your access link…</span>
                    </div>
                )}
                {state.status === "no-token" && (
                    <div className="mt-8 text-sm text-slate-600 space-y-5" data-testid="portal-no-token">
                        <div>
                            <p className="text-slate-600 font-semibold">
                                Already have an HDB account?
                            </p>
                            <p className="text-slate-500 text-xs mt-1">
                                Sign in with your registered mobile number and 4-digit MPIN.
                            </p>
                            <Link to="/portal/signin"
                                  className="mt-3 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-800 shadow-lg"
                                  data-testid="portal-cta-signin">
                                Sign in with MPIN
                            </Link>
                        </div>
                        <div className="pt-5 border-t border-slate-200">
                            <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-2">
                                New to HDB?
                            </p>
                            <Link to="/portal/register"
                                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-500 shadow-lg"
                                  data-testid="portal-cta-register">
                                <UserPlus size={14}/> Start ordering now
                            </Link>
                            <p className="text-[11px] text-slate-400 mt-2">
                                Sign up in a minute · Households and businesses both welcome · No account fee
                            </p>
                        </div>
                    </div>
                )}
                {state.status === "error" && (
                    <div className="mt-8 text-sm space-y-4" data-testid="portal-link-error">
                        <p className="text-red-700 font-semibold mb-2">Link invalid or expired</p>
                        <p className="text-slate-600">{state.error}</p>
                        <div className="pt-4 border-t border-slate-200">
                            <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-2">New here?</p>
                            <Link to="/portal/register"
                                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-800">
                                <UserPlus size={13}/> Register instead
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
